// @ts-check

/**
 * @typedef {HTMLElement & {
 *   isMenuVisible: boolean;
 *   setMenuVisibility: (isVisible: boolean, menuOpenTrigger?: HTMLElement) => void;
 *   focusActivePanel: (position: "first" | "last") => void;
 * }} MenuControllerElement
 */

class FinstralHeaderGlobal extends HTMLElement {
	/** @type {AbortController | null} */
	#abortController = null;
	/** @type {HTMLButtonElement | null} */
	#menuToggle = null;
	/** @type {MenuControllerElement | null} */
	#menuController = null;
	/** @type {HTMLDialogElement | null} */
	#ctaOverlay = null;
	/** @type {MediaQueryList | null} */
	#mql = null;
	/** @type {HTMLButtonElement | HTMLAnchorElement | null} */
	#activeCtaButton = null;
	/** @type {HTMLButtonElement | null} */
	#searchButton = null;
	/** @type {HTMLFormElement | null} */
	#searchForm = null;
	/** @type {HTMLInputElement | null} */
	#searchInput = null;
	/** @type {HTMLDialogElement | null} */
	#searchDialog = null;
	/** @type {HTMLInputElement | null} */
	#searchDialogInput = null;
	/** @type {HTMLButtonElement | null} */
	#searchClose = null;
	/** @type {HTMLElement | null} */
	#stickyCta = null;
	/** @type {IntersectionObserver | null} */
	#stickyCtaObserver = null;

	connectedCallback() {
		this.#abortController?.abort();
		this.#abortController = new AbortController();
		this.#cacheElements();
		this.#addEventListeners();
	}

	disconnectedCallback() {
		this.#abortController?.abort();
		this.#abortController = null;
		this.#activeCtaButton = null;
		this.#stickyCtaObserver?.disconnect();
		this.#stickyCtaObserver = null;
	}

	#cacheElements() {
		this.#menuToggle = /** @type {HTMLButtonElement | null} */ (
			this.querySelector(".js-HeaderGlobal-menuToggle")
		);
		this.#ctaOverlay = /** @type {HTMLDialogElement | null} */ (
			document.querySelector(".js-NavigationCtaOverlay")
		);
		this.#mql = window.matchMedia("(width < 64rem)");

		const menuId = this.#menuToggle?.getAttribute("aria-controls");
		this.#menuController = menuId
			? /** @type {MenuControllerElement | null} */ (
					document.getElementById(menuId)
				)
			: null;

		this.#searchButton = /** @type {HTMLButtonElement | null} */ (
			this.querySelector(".js-HeaderGlobal-searchButton")
		);
		this.#searchForm = /** @type {HTMLFormElement | null} */ (
			this.querySelector(".js-HeaderGlobal-searchForm")
		);
		this.#searchInput = /** @type {HTMLInputElement | null} */ (
			this.querySelector(".js-HeaderGlobal-searchInput")
		);
		this.#searchDialog = /** @type {HTMLDialogElement | null} */ (
			this.querySelector(".js-HeaderGlobal-searchDialog")
		);
		this.#searchDialogInput = /** @type {HTMLInputElement | null} */ (
			this.querySelector(".js-HeaderGlobal-searchDialogInput")
		);
		this.#searchClose = /** @type {HTMLButtonElement | null} */ (
			this.querySelector(".js-HeaderGlobal-searchClose")
		);

		this.#stickyCta = this.querySelector(".js-HeaderGlobal-stickyCta");

		this.#moveMobileCta();
		this.#observeStickyCtaObstacles();
		this.#syncMenuToggleState(this.#menuController?.isMenuVisible ?? false);
		this.#syncCtaButton();
	}

	#moveMobileCta() {
		const template = /** @type {HTMLTemplateElement | null} */ (
			this.querySelector(".js-HeaderGlobal-mobileCta")
		);
		if (!template) {
			return;
		}

		const mobileMenuFooter = this.#menuController?.querySelector(
			".Menu-mobileOverviewPanelFooter",
		);
		if (!mobileMenuFooter) {
			return;
		}

		// The menu is rendered from a cached block, so right after a deploy it can
		// still carry the static CTA that menu.twig used to print. Drop it so the
		// per-route one injected below is not duplicated.
		this.querySelector(".js-HeaderGlobal-primaryCta--mobile")?.remove();
		mobileMenuFooter.appendChild(template.content.cloneNode(true));
		template.remove();
	}

	/**
	 * Hides the sticky mobile CTA while the page footer is in view
	 * (FINSTRAL-2307). A per-route CTA that links to an anchor on the same page
	 * (the store locator's `#partner-form-id`) also hides while its target is in
	 * view, so the button never covers the form it points to.
	 */
	#observeStickyCtaObstacles() {
		this.#stickyCtaObserver?.disconnect();
		this.#stickyCtaObserver = null;

		const stickyCta = this.#stickyCta;
		if (!stickyCta) {
			return;
		}

		const obstacles = [document.querySelector(".js-Footer")];
		const href = stickyCta
			.querySelector(".js-HeaderGlobal-primaryCta--sticky")
			?.getAttribute("href");
		if (href?.startsWith("#") && href.length > 1) {
			obstacles.push(document.getElementById(href.slice(1)));
		}

		/** @type {Set<Element>} */
		const visibleObstacles = new Set();
		this.#stickyCtaObserver = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					visibleObstacles.add(entry.target);
				} else {
					visibleObstacles.delete(entry.target);
				}
			}
			stickyCta.classList.toggle(
				"HeaderGlobal-stickyCta--hidden",
				visibleObstacles.size > 0,
			);
		});

		for (const obstacle of obstacles) {
			if (obstacle) {
				this.#stickyCtaObserver.observe(obstacle);
			}
		}
	}

	/**
	 * @param {boolean} isMenuOpen
	 */
	#syncMenuToggleState(isMenuOpen) {
		this.classList.toggle("menu-visible", isMenuOpen);

		if (!this.#menuToggle) {
			return;
		}

		this.#menuToggle.setAttribute("aria-expanded", String(isMenuOpen));

		const [openIcon, closeIcon] = this.#menuToggle.querySelectorAll("svg");
		openIcon?.classList.toggle("js-menuIcon--hidden", isMenuOpen);
		closeIcon?.classList.toggle("js-menuIcon--hidden", !isMenuOpen);
	}

	#addEventListeners() {
		const signal = this.#abortController?.signal;
		if (!signal) {
			return;
		}

		this.#menuToggle?.addEventListener("click", this.#handleMenuToggleClick, {
			signal,
		});
		this.#stickyCta
			?.querySelector(".js-HeaderGlobal-primaryCta--sticky")
			?.addEventListener("click", this.#handleCtaButtonClick, { signal });
		this.#menuToggle?.addEventListener(
			"keydown",
			this.#handleMenuToggleKeydown,
			{ signal },
		);
		this.#menuController?.addEventListener(
			"finstral-menu-visibility-change",
			this.#handleMenuVisibilityChange,
			{ signal },
		);
		window.addEventListener("scroll", this.#handleScroll, {
			passive: true,
			signal,
		});
		this.#mql?.addEventListener("change", this.#handleBreakpointChange, {
			signal,
		});

		this.#searchButton?.addEventListener(
			"click",
			this.#handleSearchButtonClick,
			{ signal },
		);
		this.#searchForm?.addEventListener("keydown", this.#handleCloseOnEscape, {
			signal,
		});
		this.#searchClose?.addEventListener("click", this.#handleSearchCloseClick, {
			signal,
		});
		document.addEventListener("click", this.#handleDocumentClick, { signal });

		if (this.#searchInput) {
			this.#setupSearchValidation(this.#searchInput, signal);
		}
		if (this.#searchDialogInput) {
			this.#setupSearchValidation(this.#searchDialogInput, signal);
		}
	}

	/**
	 * @param {HTMLInputElement} input
	 * @param {AbortSignal | undefined} signal
	 */
	#setupSearchValidation(input, signal) {
		input.addEventListener(
			"invalid",
			() => {
				if (input.validity.tooShort) {
					input.setCustomValidity(input.dataset.minLengthMessage ?? "");
				}
			},
			{ signal },
		);
		input.addEventListener("input", () => input.setCustomValidity(""), {
			signal,
		});
	}

	#handleMenuToggleClick = (/** @type {Event} */ event) => {
		const menuToggle = /** @type {HTMLButtonElement | null} */ (
			event.currentTarget
		);

		if (!menuToggle || !this.#menuController) {
			return;
		}

		const willOpen = !this.#menuController.isMenuVisible;
		this.#menuController.setMenuVisibility(willOpen, menuToggle);

		// The menu dialog comes before the toggle in the DOM, so without this
		// the next Tab would skip the menu that just opened.
		if (willOpen) {
			this.#menuController.focusActivePanel("first");
		}
	};

	/**
	 * Keeps Tab inside the open mobile menu: from the toggle it continues into
	 * the menu's active panel rather than to the page behind the dialog.
	 * @param {KeyboardEvent} event
	 */
	#handleMenuToggleKeydown = (event) => {
		const menuController = this.#menuController;

		if (!menuController?.isMenuVisible || !this.#mql?.matches) {
			return;
		}

		if (event.key === "Escape") {
			menuController.setMenuVisibility(false, this.#menuToggle ?? undefined);
			return;
		}

		if (event.key === "Tab") {
			event.preventDefault();
			menuController.focusActivePanel(event.shiftKey ? "last" : "first");
		}
	};

	#handleMenuVisibilityChange = (/** @type {Event} */ event) => {
		const menuStateEvent = /** @type {CustomEvent<{ open?: boolean }>} */ (
			event
		);
		this.#syncMenuToggleState(Boolean(menuStateEvent.detail?.open));
	};

	#handleScroll = () => {
		const scrollPosition = window.scrollY || document.documentElement.scrollTop;
		const toolbarOffset = document.body.classList.contains("toolbar-fixed")
			? 53
			: 0;
		this.classList.toggle(
			"is-sticky",
			scrollPosition > this.offsetHeight + toolbarOffset,
		);
	};

	#handleCtaButtonClick = (/** @type {Event} */ event) => {
		// A per-route CTA (field_header_cta) renders as an anchor and navigates on
		// its own; Drupal omits the overlay in that case. The mobile menu sets
		// `u-modal-open`, which clips page scroll, so an in-page anchor target
		// would stay unreachable behind the open menu unless we close it first.
		if (event.currentTarget instanceof HTMLAnchorElement) {
			if (this.#menuController?.isMenuVisible) {
				this.#menuController.setMenuVisibility(false);
			}
			return;
		}

		const ctaOverlay = this.#ctaOverlay;
		if (!ctaOverlay || ctaOverlay.open) {
			return;
		}

		ctaOverlay.showModal();
	};

	/**
	 * @param {boolean} [isMobile]
	 */
	#syncCtaButton(isMobile = this.#mql?.matches ?? false) {
		const nextCtaButton =
			/** @type {HTMLButtonElement | HTMLAnchorElement | null} */ (
				this.querySelector(
					isMobile
						? ".js-HeaderGlobal-primaryCta--mobile"
						: ".js-HeaderGlobal-primaryCta--desktop",
				)
			);

		if (this.#activeCtaButton === nextCtaButton) {
			return;
		}

		this.#activeCtaButton?.removeEventListener(
			"click",
			this.#handleCtaButtonClick,
		);
		this.#activeCtaButton = nextCtaButton;
		this.#activeCtaButton?.addEventListener(
			"click",
			this.#handleCtaButtonClick,
			{
				signal: this.#abortController?.signal,
			},
		);
	}

	#handleSearchButtonClick = () => {
		if (this.#mql?.matches) {
			this.#searchDialog?.showModal();
			this.#searchDialogInput?.focus();
		} else {
			this.#openSearch();
		}
	};

	#openSearch() {
		this.#searchButton?.setAttribute("aria-expanded", "true");
		if (this.#searchButton) {
			this.#searchButton.hidden = true;
		}
		if (this.#searchForm) {
			this.#searchForm.hidden = false;
		}
		this.#searchInput?.focus();
	}

	#closeSearch() {
		const searchButton = this.#searchButton;
		const searchForm = this.#searchForm;

		searchButton?.setAttribute("aria-expanded", "false");
		if (searchForm) {
			searchForm.hidden = true;
		}
		if (searchButton) {
			searchButton.hidden = false;
			searchButton.focus();
		}
	}

	#handleCloseOnEscape = (/** @type {KeyboardEvent} */ event) => {
		if (event.key === "Escape") {
			event.preventDefault();
			this.#closeSearch();
		}
	};

	#handleSearchCloseClick = () => {
		this.#searchDialog?.close();
	};

	#handleDocumentClick = (/** @type {MouseEvent} */ event) => {
		if (this.#searchForm?.hidden) {
			return;
		}
		const wrapper = this.querySelector(".HeaderGlobal-searchWrapper");
		if (wrapper && !wrapper.contains(/** @type {Node} */ (event.target))) {
			this.#closeSearch();
		}
	};

	/**
	 * @param {MediaQueryListEvent} event
	 */
	#handleBreakpointChange = (event) => {
		this.#syncCtaButton(event.matches);
		if (!this.#searchForm?.hidden) {
			this.#closeSearch();
		}
		if (!event.matches && this.#searchDialog?.open) {
			this.#searchDialog.close();
		}
	};
}

if (!customElements.get("finstral-header-global")) {
	customElements.define("finstral-header-global", FinstralHeaderGlobal);
}
